import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import { AuthContext } from '../context/authContext';
import { useEstadoApp } from '../context/EstadoAppContext';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { initiateSocketConnection } from '../service/socketio';
import { useNotificaciones } from '../hooks/useNotificaciones';
import NotificacionesFlotante from './NotificacionesFlotante';

// Burbuja global de notificaciones de pagos y certificaciones.
//
// Antes este componente no existía: NotificacionesFlotante y NotificacionesPanel estaban
// escritos pero NO se montaban en ninguna parte de la app. Como useNotificaciones solo
// corre dentro de esos dos componentes, el hook nunca se ejecutaba, y por eso no
// pasaba ninguna de las tres cosas:
//
//   - la carga inicial de GET /api/notificaciones (o sea, nada al recargar),
//   - la suscripción a 'notificacion_nueva' / 'pago_realizado' (o sea, nada en
//     tiempo real),
//   - y el toast, que se dispara desde adentro del handler del socket.
//
// El backend sí guardaba y emitía bien (la fila existía y llegaba a la sala del rol
// destino); lo que faltaba era el componente que la pintara. Por eso el único cartel
// que se veía era el de "Edición", que sale por otro camino.
//
// El hook se llama ACÁ y no adentro de NotificacionesFlotante, a propósito: el
// flotante devuelve null cuando no hay nada pendiente, y si el hook viviera ahí, con
// cero notificaciones el componente ni existiría y nunca se suscribiría al socket. O
// sea que el primer aviso de la sesión se perdía siempre y solo aparecía al recargar.
// El componente que escucha tiene que existir siempre; solo lo que se pinta puede
// desaparecer.
//
// Se monta en LayoutPriv, junto a ChatBurbujaGlobal, para que exista en todas las
// páginas privadas sin importar en cuál esté el usuario.
const NotificacionesGlobal = () => {
    const { user, role } = useContext(AuthContext) || {};
    const [socket, setSocket] = useState(null);

    const navigate = useNavigate();
    const { cambiarEstadoApp } = useEstadoApp();
    const { setItem } = useLocalStorage();

    // El socket se abre acá y no en cada página: las páginas que llaman a
    // initiateSocketConnection reciben esta misma conexión (la función reutiliza la que
    // ya está viva).
    useEffect(() => {
        if (!user || !role) return;
        setSocket(initiateSocketConnection());
    }, [user, role]);

    // Sin sesión no hay a quién avisarle, y sin socket no hay a quién escuchar.
    // useNotificaciones se llama igual en todos los casos para no romper el orden de
    // hooks: adentro ya se encarga de no hacer nada si no hay usuario.
    const {
        notificaciones,
        notificacionesNoLeidas,
        marcarComoLeida,
        marcarTodasComoLeidas,
    } = useNotificaciones(socket);

    // Al hacer clic en un aviso se abre la obra, con el mismo patrón que
    // ChatBurbujaGlobal y TablaObras: se dejan los datos en localStorage, se cambia el
    // estado de la app y recién después se navega, porque InicioPage lee el query param
    // solo si estadoApp es 'edit_desde_pagos' o 'edit_desde_proyectos'.
    const handleNavigate = (notificacion) => {
        const idPartidoObra = notificacion?.id_partido_obra;
        if (!idPartidoObra) return;

        setItem('idPartidoObra', idPartidoObra);
        setItem('isEditingPagos', 'false');
        cambiarEstadoApp('edit_desde_pagos', {
            obra: idPartidoObra,
            origen: 'notificaciones',
        });
        navigate(`/inicio?id_partido_obra=${encodeURIComponent(idPartidoObra)}`);
    };

    if (!user || !role) return null;

    return (
        <NotificacionesFlotante
            notificaciones={notificaciones}
            notificacionesNoLeidas={notificacionesNoLeidas}
            marcarComoLeida={marcarComoLeida}
            marcarTodasComoLeidas={marcarTodasComoLeidas}
            onNavigate={handleNavigate}
        />
    );
};

export default NotificacionesGlobal;